import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  Stack,
  Button,
  Link,
  useColorMode,
  useColorStyle,
  Space,
  Grid,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@tonic-one/react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLoginUserStore } from '../../../models/useLoginUserModel';
import { THIRD_PARTY_LINK } from '../../../utils/casDocLink';
import { BannerType } from '../../../utils/enums';
import { gotoConnectToV1, gotoV1ForEveryone } from './shared';

type PopupType = BannerType | 'v1ForEveryone';
interface Props {
  bannerType: PopupType;
}

function UpgradeModal({ bannerType }: Props) {
  const { t } = useTranslation('header', {
    keyPrefix: 'header.banner.modal',
  });
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [isOpen] = useState(true);
  const { isRootUser, user } = useLoginUserStore();

  const renderHowToUpdate = () => {
    if (!isRootUser) {
      return (
        <>
          <Space height="4px" />
          <Text>
            {t('how.p4', {
              accountEmail: user?.accountEmail,
              accountName: user?.accountName,
            })}
          </Text>
        </>
      );
    }

    if (bannerType === BannerType.Flywheel || bannerType === 'v1ForEveryone') {
      return (
        <>
          <Grid templateColumns="20px 1fr" gap="4px">
            <Text>1.</Text>
            <Text>
              <Trans i18nKey="header.banner.modal.how.p1">
                placeholder
                <Link
                  href={THIRD_PARTY_LINK.howToUpdateV1InitialLicenseVideo}
                  target="_blank"
                >
                  placeholder
                </Link>
              </Trans>
            </Text>
            <Text>2.</Text>
            <Text>
              <Trans i18nKey="header.banner.modal.how.p2">
                placeholder
                <b>placeholder</b>
                placeholder
                <Link
                  href={THIRD_PARTY_LINK.howToUpdateV1ConnectProductVideo}
                  target="_blank"
                >
                  placeholder
                </Link>
              </Trans>
            </Text>
            <Text>3.</Text>
            <Text>
              <Trans i18nKey="header.banner.modal.how.p3">
                placeholder
                <Link
                  href={THIRD_PARTY_LINK.howToUpdateV1UpdateToProductVideo}
                  target="_blank"
                >
                  placeholder
                </Link>
              </Trans>
            </Text>
          </Grid>
          <Space height="16px" />
          <Text color={colorStyle.color.primary}>
            {t('how.contact')}
            <Popover trigger="hover" placement="top">
              <PopoverTrigger>
                <Text display="inline-block" ml={8} color="#6F9BF4">
                  {t('how.contactInfo')}
                </Text>
              </PopoverTrigger>
              <PopoverContent>{t('how.contactEmail')}</PopoverContent>
            </Popover>
          </Text>
        </>
      );
    }

    return (
      <>
        <Text>
          <Trans i18nKey="header.banner.modal.how.p5">
            placeholder
            <strong>placeholder</strong>placeholder
            <a
              target="_blank"
              href="https://docs.trendmicro.com/en-us/documentation/article/trend-vision-one-get-start-email-collaboration"
            >
              placeholder
            </a>
          </Trans>
        </Text>
        <Space height="4px" />
        <Text>
          <Trans i18nKey="header.banner.modal.how.p6">
            placeholder
            <Link
              href={THIRD_PARTY_LINK.howToUpdateV1ConnectProductVideo}
              target="_blank"
            >
              placeholder
            </Link>
            placeholder
            <Link
              href={THIRD_PARTY_LINK.howToUpdateV1UpdateToProductVideo}
              target="_blank"
            >
              placeholder
            </Link>
          </Trans>
        </Text>
        <Space height="16px" />
        <Text color={colorStyle.color.primary}>
          {t('how.contact')}
          <Popover trigger="hover" placement="top">
            <PopoverTrigger>
              <Text display="inline-block" ml={8} color="#6F9BF4">
                {t('how.contactInfo')}
              </Text>
            </PopoverTrigger>
            <PopoverContent>{t('how.contactEmail')}</PopoverContent>
          </Popover>
        </Text>
      </>
    );
  };

  const onUpdateClick = () => {
    // isn't a V1 customer, The update now button will follow our flywheel signup flow to guide customers to register V1 account
    if (bannerType === BannerType.Flywheel) {
      // gotoFlywheelSignup(notificationContent);
      gotoV1ForEveryone();
      return;
    }

    if (bannerType === 'v1ForEveryone') {
      gotoV1ForEveryone();
      return;
    }
    //is V1 customer：The update now button will redirect to V1ECS-CECP landing page to guide customers to do solution update
    gotoConnectToV1();
  };

  return (
    <Modal
      id="upgradePopup"
      isOpen={isOpen}
      size="md"
      /**
       * tonic zIndex link: https://trendmicro-frontend.github.io/tonic-ui/react/v2/theme/z-indices/
       * tonic modal default z-index is 1500, upgradePopup has higher priority
       */
      zIndex={1501}
      color={colorStyle.color.primary}
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent>
        <ModalBody
          display="flex"
          flexDirection="column"
          alignItems="center"
          m="0 !important"
        >
          <Space height="36px" />
          {/* title */}
          <Text size="2xl">{t('title')}</Text>
          <Space height="32px" />

          {/* sub title */}
          <Text
            textAlign="left"
            fontSize="14px"
            fontWeight={400}
            dangerouslySetInnerHTML={{ __html: t('subTitle') }}
          />
          <Space height="32px" />
          <Text
            textAlign="left"
            fontSize="14px"
            fontWeight={400}
            dangerouslySetInnerHTML={{ __html: t('note') }}
          />
          {/* how update */}
          <Space height="16px" />
          <Text alignSelf="flex-start" fontWeight="semibold">
            {t('how.title')}
          </Text>
          <Stack alignSelf="flex-start" color={colorStyle.color.secondary}>
            {renderHowToUpdate()}
          </Stack>

          {/*<Space height="36px" />*/}

          {/* update button */}
          {isRootUser && (
            <>
              <Space height="36px" />
              <Button
                py="12px"
                px="60px"
                size="lg"
                border="none"
                variant="primary"
                alignSelf="stretch"
                onClick={onUpdateClick}
              >
                {t('update')}
              </Button>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default UpgradeModal;
